import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  FileSpreadsheet, 
  MessageCircle, 
  Zap, 
  CheckCircle2, 
  Clock,
  Smartphone
} from 'lucide-react';

export const ChaosToOrderAnimation: React.FC = () => {
  const [isOrdered, setIsOrdered] = useState(false);

  // Auto-trigger order after a delay for impact, or allow manual trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsOrdered(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const chaoticIcons = [
    { Icon: Phone, x: -80, y: -60, rotate: -15, color: 'text-gray-400', delay: 0 },
    { Icon: Mail, x: 60, y: -40, rotate: 10, color: 'text-gray-500', delay: 0.1 },
    { Icon: FileSpreadsheet, x: -40, y: 50, rotate: -5, color: 'text-gray-400', delay: 0.2 },
    { Icon: MessageCircle, x: 70, y: 60, rotate: 20, color: 'text-gray-500', delay: 0.3 },
  ];

  return (
    <div 
      className="relative w-full h-[500px] flex items-center justify-center"
      onMouseEnter={() => setIsOrdered(true)}
    >
      {/* Background/Context */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent rounded-full blur-3xl opacity-20" />

      {/* The "Line of Light" - Victor's System */}
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ 
          height: isOrdered ? '80%' : 0, 
          opacity: isOrdered ? 1 : 0 
        }}
        transition={{ duration: 1, ease: "easeInOut" }}
        className="absolute w-1 bg-gradient-to-b from-transparent via-cyan-400 to-transparent shadow-[0_0_20px_rgba(34,211,238,0.6)] z-10"
      />

      {/* Icons Container */}
      <div className="relative z-20 w-full max-w-md h-full flex flex-col items-center justify-center">
        {chaoticIcons.map((item, index) => (
          <motion.div
            key={index}
            initial={{ x: item.x, y: item.y, rotate: item.rotate, opacity: 0.5 }}
            animate={{ 
              x: isOrdered ? 0 : item.x, 
              y: isOrdered ? (index - 1.5) * 80 : item.y, // Distribute vertically
              rotate: isOrdered ? 0 : item.rotate,
              opacity: 1,
              scale: isOrdered ? 1.2 : 1
            }}
            transition={{ duration: 0.8, ease: "backOut" }}
            className={`absolute p-4 rounded-xl backdrop-blur-md border ${
              isOrdered 
                ? 'bg-slate-900/80 border-cyan-500/50 text-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.3)]' 
                : 'bg-gray-800/50 border-gray-700/50 ' + item.color
            }`}
          >
            <item.Icon size={24} />
          </motion.div>
        ))}

        {/* Floating Cards - Relatable Scenarios appearing after order */}
        <AnimatePresence>
          {isOrdered && (
            <>
              {/* Card 1: Automated Response */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="absolute top-[15%] right-[-20%] md:right-[-30%] w-64 bg-slate-900/90 border border-emerald-500/30 p-4 rounded-xl shadow-xl backdrop-blur-xl"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-500/20 rounded-lg text-green-400">
                    <Smartphone size={18} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-1">Richiesta ore 21:00</div>
                    <div className="text-sm text-white font-medium mb-2">Risposta Automatica Inviata</div>
                    <div className="flex items-center gap-2 text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full w-fit">
                      <CheckCircle2 size={12} />
                      Cliente Trattenuto
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Card 2: Speed */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.3, duration: 0.5 }}
                className="absolute bottom-[20%] left-[-20%] md:left-[-30%] w-64 bg-slate-900/90 border border-cyan-500/30 p-4 rounded-xl shadow-xl backdrop-blur-xl"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-cyan-500/20 rounded-lg text-cyan-400">
                    <Zap size={18} />
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-1">Tempo Caricamento</div>
                    <div className="text-2xl text-white font-bold mb-1">0.4s</div>
                    <div className="text-xs text-slate-300">
                      3x più veloce dei competitor
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

